import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthStateShape, AuthUser } from './types';
import { AuthService } from './AuthService';
import { authEmitter } from './emitter';

const initialState: AuthStateShape = {
  user: null,
  access: null,
  refresh: null,
  needsPasswordChange: false,
  loading: true,
  error: null,
  isAdmin: false,
};

function getRoleFlags(user: AuthUser | null): { isAdmin: boolean } {
  const isAdmin = !!(user?.is_staff || user?.is_superuser);
  return { isAdmin };
}

interface AuthContextValue extends AuthStateShape {
  login: (username: string, password: string) => Promise<void>;
  changePassword: (oldPwd: string, newPwd: string) => Promise<void>;
  activateAccount: (uid: string, token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Decode JWT payload without verification (for extracting user info)
function decodeJwtPayload(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

// Try to restore user from stored token
function getUserFromToken(token: string | null): AuthUser | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  
  // Also check localStorage for stored user info (saved on login)
  let storedUser: any = null;
  try {
    const stored = localStorage.getItem('auth.user');
    if (stored) storedUser = JSON.parse(stored);
  } catch {}
  
  return {
    id: payload.user_id || storedUser?.id || 'unknown',
    username: storedUser?.username || `User ${payload.user_id}`,
    is_staff: storedUser?.is_staff,
    is_superuser: storedUser?.is_superuser,
    role: payload.role || storedUser?.role,
    groups: Array.isArray(payload.groups) ? payload.groups : storedUser?.groups,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthStateShape>(initialState);

  useEffect(() => {
    let mounted = true;
    AuthService.init()
      .then(() => {
        if (!mounted) return;
        const access = AuthService.getAccess();
        const refresh = AuthService.getRefresh();
        const user = getUserFromToken(access);
        const { isAdmin } = getRoleFlags(user);
        setState((s: AuthStateShape) => ({ 
          ...s, 
          access, 
          refresh, 
          user,
          isAdmin,
          loading: false 
        }));
      })
      .catch((err) => {
        console.error('[AuthProvider] Init failed:', err);
        if (!mounted) return;
        setState((s: AuthStateShape) => ({ ...s, loading: false, error: 'Failed to initialize authentication' }));
      });
    const off = authEmitter.on(evt => {
      if (!mounted) return;
      if (evt.type === 'tokens') {
        const user = getUserFromToken(evt.access);
        setState((s: AuthStateShape) => ({
          ...s,
          access: evt.access,
          refresh: evt.refresh,
          user: user || s.user,
          ...(function() {
            const nextUser = user || s.user;
            const { isAdmin } = getRoleFlags(nextUser);
            return { isAdmin };
          })(),
        }));
      } else if (evt.type === 'login') {
        setState({ ...evt.state, error: null });
      } else if (evt.type === 'logout') {
        setState({ ...initialState, loading: false });
      } else if (evt.type === 'passwordChanged') {
        setState((s: AuthStateShape) => ({ ...s, needsPasswordChange: false }));
      } else if (evt.type === 'error') {
        setState((s: AuthStateShape) => ({ ...s, error: evt.error }));
      }
    });
    return () => { mounted = false; off(); };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setState((s: AuthStateShape) => ({ ...s, loading: true, error: null }));
    try {
      const res = await AuthService.login({ username, password });
      const user: AuthUser = {
        id: res.user_id,
        username: res.username,
        is_staff: res.is_staff,
        is_superuser: res.is_superuser,
      };
      const { isAdmin } = getRoleFlags(user);
      // Store user info for restoration on refresh
      try {
        localStorage.setItem('auth.user', JSON.stringify(user));
      } catch {}
      setState({
        user,
        access: res.access,
        refresh: res.refresh,
        needsPasswordChange: res.force_password_change,
        loading: false,
        error: null,
        isAdmin,
      });
    } catch (e: any) {
      setState((s: AuthStateShape) => ({ ...s, loading: false, error: e.message || 'Login failed' }));
    }
  }, []);

  const changePassword = useCallback(async (oldPwd: string, newPwd: string) => {
    setState((s: AuthStateShape) => ({ ...s, loading: true, error: null }));
    try {
      const res = await AuthService.changePassword({ old_password: oldPwd, new_password: newPwd });
      setState((s: AuthStateShape) => ({ ...s, access: res.access, refresh: res.refresh || s.refresh, needsPasswordChange: false, loading: false }));
    } catch (e: any) {
      setState((s: AuthStateShape) => ({ ...s, loading: false, error: e.message || 'Change password failed' }));
    }
  }, []);

  const activateAccount = useCallback(async (uid: string, token: string, password: string) => {
    setState((s: AuthStateShape) => ({ ...s, loading: true, error: null }));
    try {
      const res = await AuthService.activateAccount({ uid, token, new_password: password });
      const user: AuthUser = {
        id: res.id,
        username: res.username,
        is_staff: res.is_staff,
        is_superuser: res.is_superuser,
      };
      const { isAdmin } = getRoleFlags(user);
      try {
        localStorage.setItem('auth.user', JSON.stringify(user));
      } catch {}
      setState({
        user,
        access: res.access,
        refresh: res.refresh,
        needsPasswordChange: false,
        loading: false,
        error: null,
        isAdmin,
      });
    } catch (e: any) {
      let message = e.message || 'Account activation failed';
      const data = e.data || {};
      if (data.token) message = Array.isArray(data.token) ? data.token.join(', ') : String(data.token);
      else if (data.new_password) message = Array.isArray(data.new_password) ? data.new_password.join(', ') : String(data.new_password);
      setState((s: AuthStateShape) => ({ ...s, loading: false, error: message }));
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    setState((s: AuthStateShape) => ({ ...s, loading: true }));
    await AuthService.logout();
    // Clear stored user info
    try { localStorage.removeItem('auth.user'); } catch {}
    setState({ ...initialState, loading: false });
  }, []);

  return <AuthContext.Provider value={{ ...state, login, changePassword, activateAccount, logout }}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
